import { getcountries } from '@/api/common';
import { fetchGhlPaymentZones } from '@/api/ghlPayment.api';

export const PH_COUNTRY_ID = 168;

export function emptyAddress() {
  return {
    address1: '',
    city: '',
    postcode: '',
    countryId: PH_COUNTRY_ID,
    zoneId: 0
  };
}

export function toPositiveInt(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeName(value) {
  return String(value || '').trim().toLowerCase();
}

export function findCountryId(countries, value) {
  const list = Array.isArray(countries) ? countries : [];
  const id = toPositiveInt(value);
  if (id && list.some((country) => Number(country.country_id) === id)) {
    return id;
  }

  const needle = normalizeName(value);
  if (!needle) return 0;

  const match = list.find((country) => (
    normalizeName(country.name) === needle
    || normalizeName(country.iso_code_2) === needle
    || normalizeName(country.iso_code_3) === needle
  ));
  return match ? Number(match.country_id) : 0;
}

export function findZoneId(zones, value) {
  const list = Array.isArray(zones) ? zones : [];
  const id = toPositiveInt(value);
  if (id && list.some((zone) => Number(zone.zone_id) === id)) {
    return id;
  }

  const needle = normalizeName(value);
  if (!needle) return 0;

  const match = list.find((zone) => normalizeName(zone.name) === needle);
  return match ? Number(match.zone_id) : 0;
}

export function zoneName(zones, zoneId) {
  const id = toPositiveInt(zoneId);
  const match = (Array.isArray(zones) ? zones : []).find((zone) => Number(zone.zone_id) === id);
  return match ? String(match.name || '') : '';
}

export async function fetchCountries() {
  try {
    const response = await getcountries();
    return Array.isArray(response && response.data && response.data.countries)
      ? response.data.countries
      : [];
  } catch {
    return [];
  }
}

const ZONE_CACHE_PREFIX = 'ghl:pay:zones:';
const ZONE_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function zoneCacheKey(countryId) {
  return `${ZONE_CACHE_PREFIX}${countryId}`;
}

function loadCachedZones(countryId) {
  try {
    const raw = localStorage.getItem(zoneCacheKey(countryId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.zones) || !parsed.zones.length) return null;

    const savedAt = Number(parsed.savedAt || 0);
    if (!savedAt || Date.now() - savedAt > ZONE_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(zoneCacheKey(countryId));
      return null;
    }

    return parsed.zones;
  } catch {
    return null;
  }
}

function saveCachedZones(countryId, zones) {
  try {
    localStorage.setItem(zoneCacheKey(countryId), JSON.stringify({
      savedAt: Date.now(),
      zones
    }));
  } catch {
    // Safari private mode and quota errors are non-fatal.
  }
}

export async function fetchZones(countryId) {
  const id = toPositiveInt(countryId);
  if (!id) return [];

  const cached = loadCachedZones(id);
  if (cached) return cached;

  try {
    const { data } = await fetchGhlPaymentZones(id);
    const zones = Array.isArray(data && data.zones) ? data.zones : [];
    if (zones.length) saveCachedZones(id, zones);
    return zones;
  } catch {
    return [];
  }
}
